import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { Bucket } from "../types";
import type { GroupedLineFacet, ChartDataPoint } from "../utils/chartData";
import type { DrillClick } from "../utils/drilldown";
import { formatDisplayDate } from "../utils/chartFormatters";
import { EmptyState } from "../../../ui/EmptyState";
import { ChartSkeleton } from "../../../ui/ChartSkeleton";
import { useChartTheme } from "../../theme";
// Direct imports (not the feature barrel) so jest doesn't transitively pull in
// import.meta-using modules; kept consistent with the chart components.
import { TagValue } from "../../tag-aliases/TagValue";
import { formatTagValue } from "../../tag-aliases/formatTagValue";
import { useTagAliases } from "../../tag-aliases/context";
import { LineChartTooltip } from "./charts/LineChartTooltip";
import { makeClickableDot } from "./charts/clickableDot";

interface GroupedTimeSeriesChartProps {
  facets: GroupedLineFacet[];
  bucket: Bucket;
  groupColors: Record<string, string>;
  groupByKey: string;
  /** criteria → display label (evaluator name) for facet titles. */
  labels?: Record<string, string>;
  loading?: boolean;
  /** When provided, clicking a group's line drills into the filtered Traces view. */
  onDrill?: (click: DrillClick) => void;
}

const groupReactKey = (group: string) => (group === "" ? "__empty__" : group);

/**
 * Grouped time-series as small multiples: one line chart per criterion, one
 * line per tag value. A single shared legend gives every tag value one colour
 * across all facets (via groupColors).
 */
export function GroupedTimeSeriesChart({
  facets,
  bucket,
  groupColors,
  groupByKey,
  labels = {},
  loading = false,
  onDrill,
}: GroupedTimeSeriesChartProps) {
  const chart = useChartTheme();
  // Display only: `group` stays the dataKey, the colour key and the drill-down
  // value; the alias reaches the tooltip through the Line's `name`.
  const { aliases } = useTagAliases();

  if (loading) {
    return <ChartSkeleton title="Score trends by group" variant="line" />;
  }

  if (facets.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <EmptyState
          message="No data for this grouping"
          description={`No evaluations are tagged with “${groupByKey}” for the current filters.`}
          variant="info"
        />
      </div>
    );
  }

  const legendGroups = Array.from(
    new Set(facets.flatMap((f) => f.groupNames)),
  ).sort();

  return (
    <div className="space-y-4">
      <div
        className="flex flex-wrap gap-x-4 gap-y-1"
        data-testid="grouped-legend"
      >
        {legendGroups.map((group) => (
          <span
            key={groupReactKey(group)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground"
          >
            <span
              className="inline-block w-3 h-0.5 rounded"
              style={{ backgroundColor: groupColors[group] }}
            />
            <TagValue value={group} />
          </span>
        ))}
      </div>

      {/* Responsive grid of small multiples — ~5 across on a wide desktop
          (2 rows at the 10-criteria cap), collapsing to fewer columns on
          narrower screens; a single facet stretches to full width. */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-4">
        {facets.map((facet) => (
          <div
            key={facet.criteria}
            className="bg-card rounded-lg shadow-sm border border-border p-4"
            data-testid="grouped-facet"
          >
            <h3
              className="text-sm font-semibold mb-2 truncate"
              title={facet.criteria}
            >
              {labels[facet.criteria] ?? facet.criteria}
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={facet.dataPoints}
                margin={{ top: 5, right: 12, left: -8, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis
                  dataKey="date"
                  stroke={chart.axis}
                  tick={{ fill: chart.axis, fontSize: 10 }}
                  tickFormatter={(value: string) =>
                    formatDisplayDate(value, bucket)
                  }
                  angle={-45}
                  textAnchor="end"
                  height={48}
                  interval="preserveStartEnd"
                  minTickGap={16}
                />
                <YAxis
                  domain={[0, 1]}
                  ticks={[0, 0.5, 1]}
                  width={28}
                  stroke={chart.axis}
                  tick={{ fill: chart.axis, fontSize: 10 }}
                />
                <Tooltip content={<LineChartTooltip bucket={bucket} />} />
                {facet.groupNames.map((group) => {
                  const onSelect = onDrill
                    ? (payload: any) =>
                        onDrill({
                          type: "groupBucket",
                          criteria: facet.criteria,
                          groupKey: groupByKey,
                          groupValue: group,
                          bucketStart:
                            typeof payload?.date === "string"
                              ? payload.date
                              : undefined,
                          bucket,
                        })
                    : undefined;
                  return (
                    <Line
                      // Function dataKey so empty-string group values still
                      // resolve (Recharts' string dataKey getter drops "" path).
                      key={groupReactKey(group)}
                      type="natural"
                      dataKey={(entry: ChartDataPoint) =>
                        entry[group] as number | null
                      }
                      stroke={groupColors[group]}
                      strokeWidth={1.75}
                      // When drillable, render small clickable dots (each carries
                      // its own onClick via makeClickableDot — a Line-level
                      // onClick doesn't fire from the line); otherwise keep the
                      // dot-free look. The activeDot needs the same handler
                      // because recharts paints it over the dot on hover and
                      // would otherwise swallow the click.
                      dot={
                        onSelect
                          ? makeClickableDot({
                              color: groupColors[group],
                              radius: 2.75,
                              keyPrefix: `gdot-${groupReactKey(group)}`,
                              onSelect,
                            })
                          : false
                      }
                      activeDot={
                        onSelect
                          ? makeClickableDot({
                              color: groupColors[group],
                              radius: 4,
                              keyPrefix: `gadot-${groupReactKey(group)}`,
                              onSelect,
                            })
                          : { r: 4 }
                      }
                      name={formatTagValue(group, aliases)}
                      connectNulls={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  );
}
